import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight } from "lucide-react";
import { WAYS_TO_RETURN } from "@/pages/soul/nervous/nervousContent";

/**
 * WaysToReturn — a quiet grounding toolkit shown alongside the primary
 * Nervous System journey. Four practices, each expanding inline. A
 * subtle link to Breath appears at the bottom for users who want a
 * structured breathing practice.
 */
export default function WaysToReturn() {
  const [openSlug, setOpenSlug] = useState(null);
  return (
    <section data-testid="ns-ways-to-return" className="reveal reveal-1">
      <div className="max-w-2xl">
        <div className="text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--copper))]">
          Ways to Return
        </div>
        <h2 className="font-display text-3xl md:text-4xl mt-3 tracking-tight italic">
          When you want something simple.
        </h2>
        <p className="mt-4 text-[15px] leading-relaxed text-[hsl(var(--fg))]/80">
          A small grounding toolkit for the moments when the journey would be too much and one gentle
          practice would be enough.
        </p>
      </div>

      <ul className="mt-8 divide-y divide-[hsl(var(--line))] border-y border-[hsl(var(--line))]">
        {WAYS_TO_RETURN.map((t) => {
          const isOpen = openSlug === t.slug;
          return (
            <li key={t.slug}>
              <button
                type="button"
                onClick={() => setOpenSlug(isOpen ? null : t.slug)}
                data-testid={`ns-tool-${t.slug}`}
                aria-expanded={isOpen}
                className="w-full grid grid-cols-[1fr_auto] items-baseline gap-4 py-6 md:py-7 px-2 md:px-4 text-left group hover:bg-[hsl(var(--sand))]/30 transition-colors"
              >
                <div>
                  <h3 className="font-display text-2xl md:text-3xl tracking-tight text-[hsl(var(--fg))]">
                    {t.title}
                  </h3>
                  <p className="mt-2 text-[14.5px] leading-relaxed text-[hsl(var(--fg))]/70 max-w-xl">
                    {t.tagline}
                  </p>
                </div>
                <ChevronDown
                  size={18}
                  className={`text-[hsl(var(--copper))] transition-transform ${
                    isOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              {isOpen && (
                <div
                  className="pb-8 px-2 md:px-4 max-w-3xl reveal reveal-1"
                  data-testid={`ns-tool-body-${t.slug}`}
                >
                  <p className="text-[15.5px] leading-relaxed text-[hsl(var(--fg))]/85">
                    {t.body}
                  </p>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="mt-8 text-center">
        <Link
          to="/soul/regulation-presence/breath"
          data-testid="ns-explore-breath-link"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--copper))] hover:opacity-80 transition-opacity"
        >
          <span>Explore Breath</span>
          <ArrowRight size={12} />
        </Link>
        <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))] italic">
          If you'd like a structured breathing practice.
        </p>
      </div>
    </section>
  );
}
